export type InputSnapshot = {
  keys: Readonly<Record<string, boolean>>;
  mouseButtons: Readonly<Record<number, boolean>>;
  mouseDX: number;
  mouseDY: number;
  scrollDelta: number;
  pointerLocked: boolean;
};

export class Input {
  private readonly keys: Record<string, boolean> = {};
  private readonly mouseButtons: Record<number, boolean> = {};
  private accMouseDX = 0;
  private accMouseDY = 0;
  private accScrollDelta = 0;
  private pointerLocked = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    window.addEventListener("mousemove", this.onMouseMove);
    window.addEventListener("mousedown", this.onMouseDown);
    window.addEventListener("mouseup", this.onMouseUp);
    window.addEventListener("wheel", this.onWheel, { passive: true });
    document.addEventListener("pointerlockchange", this.onPointerLockChange);
    canvas.addEventListener("click", this.requestLock);
  }

  dispose(): void {
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    window.removeEventListener("mousemove", this.onMouseMove);
    window.removeEventListener("mousedown", this.onMouseDown);
    window.removeEventListener("mouseup", this.onMouseUp);
    window.removeEventListener("wheel", this.onWheel);
    document.removeEventListener("pointerlockchange", this.onPointerLockChange);
    this.canvas.removeEventListener("click", this.requestLock);
  }

  consume(): InputSnapshot {
    const snap: InputSnapshot = {
      keys: { ...this.keys },
      mouseButtons: { ...this.mouseButtons },
      mouseDX: this.accMouseDX,
      mouseDY: this.accMouseDY,
      scrollDelta: this.accScrollDelta,
      pointerLocked: this.pointerLocked,
    };
    this.accMouseDX = 0;
    this.accMouseDY = 0;
    this.accScrollDelta = 0;
    return snap;
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    this.keys[e.code] = true;
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys[e.code] = false;
  };

  private onMouseDown = (e: MouseEvent): void => {
    this.mouseButtons[e.button] = true;
  };

  private onMouseUp = (e: MouseEvent): void => {
    this.mouseButtons[e.button] = false;
  };

  private onWheel = (e: WheelEvent): void => {
    this.accScrollDelta += Math.sign(e.deltaY);
  };

  private onMouseMove = (e: MouseEvent): void => {
    if (!this.pointerLocked) return;
    this.accMouseDX += e.movementX;
    this.accMouseDY += e.movementY;
  };

  private onPointerLockChange = (): void => {
    this.pointerLocked = document.pointerLockElement === this.canvas;
  };

  private requestLock = (): void => {
    if (!this.pointerLocked) this.canvas.requestPointerLock();
  };
}
